import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";

import { AdminPage } from "@/components/admin/AdminSidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useUserRoles } from "@/hooks/use-user-roles";
import { EstimadorViviendaWizard } from "@/components/estimador-vivienda/EstimadorViviendaWizard";
import {
  importarNotariadoCp,
  resumenNotariadoCp,
} from "@/lib/estimador-residencial/notariado.functions";
import {
  actualizarMivau,
  importarRegistradores,
  resumenReferencias,
} from "@/lib/estimador-residencial/referencias.functions";

export const Route = createFileRoute("/admin/estimador-vivienda")({
  head: () => ({
    meta: [
      { title: "Estimador de vivienda — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: EstimadorViviendaAdminPage,
});

function EstimadorViviendaAdminPage() {
  const { roles } = useUserRoles();
  const isAdmin = (roles ?? []).includes("admin");

  if (!isAdmin) {
    return (
      <AdminPage title="Estimador de vivienda">
        <p className="text-sm text-muted-foreground">
          Esta herramienta solo está disponible para administradores.
        </p>
      </AdminPage>
    );
  }

  return (
    <AdminPage
      title="Estimador de vivienda"
      description="Precio de venta o alquiler de pisos y casas con anuncios comparables en directo. Independiente del valorador de locales."
      actions={
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/herramientas">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Herramientas
          </Link>
        </Button>
      }
    >
      <div className="grid gap-8">
        <EstimadorViviendaWizard />
        <ImportadorNotariado />
        <ReferenciasCierre />
      </div>
    </AdminPage>
  );
}

function ImportadorNotariado() {
  const importar = useServerFn(importarNotariadoCp);
  const resumenFn = useServerFn(resumenNotariadoCp);
  const resumen = useQuery({ queryKey: ["notariado-cp-resumen"], queryFn: () => resumenFn() });
  const [csv, setCsv] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function enviar() {
    setEnviando(true);
    try {
      const r = await importar({ data: { csv } });
      if (!r.ok) {
        toast.error(r.error);
      } else {
        toast.success(
          `${r.importadas} filas importadas${r.errores.length ? `, ${r.errores.length} con errores` : ""}`,
        );
        setCsv("");
        resumen.refetch();
      }
      if ("errores" in r && r.errores && r.errores.length) {
        console.warn("[notariado] filas con errores", r.errores);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo importar");
    } finally {
      setEnviando(false);
    }
  }

  async function leerArchivo(file: File) {
    setCsv(await file.text());
  }

  const r = resumen.data;
  return (
    <Card>
      <CardContent className="grid gap-4 p-6">
        <div>
          <h2 className="text-base font-semibold">
            Precio escriturado por código postal (Notariado)
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Corrige el precio de cierre de las ventas. Descarga los datos del Portal Estadístico del
            Notariado y pégalos con el formato <code>cp;periodo;eur_m2;n_operaciones</code> (por
            ejemplo <code>46005;2026-06;3950,5;42</code>).
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {resumen.isLoading
              ? "Cargando…"
              : r && r.ok
                ? `${r.filas} filas cargadas${r.ultimo_periodo ? ` · último periodo ${r.ultimo_periodo}` : ""}`
                : "Sin datos cargados todavía."}
          </p>
        </div>
        <Textarea
          rows={6}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          placeholder={"cp;periodo;eur_m2;n_operaciones\n46005;2026-06;3950,5;42"}
          className="font-mono text-xs"
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="cursor-pointer text-sm text-primary hover:underline">
            <input
              type="file"
              accept=".csv,.txt,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void leerArchivo(file);
              }}
            />
            Cargar archivo CSV
          </label>
          <Button onClick={enviar} disabled={!csv.trim() || enviando}>
            {enviando ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Importar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ReferenciasCierre() {
  const actualizar = useServerFn(actualizarMivau);
  const importar = useServerFn(importarRegistradores);
  const resumenFn = useServerFn(resumenReferencias);
  const resumen = useQuery({
    queryKey: ["referencias-cierre-resumen"],
    queryFn: () => resumenFn(),
  });
  const [actualizando, setActualizando] = useState(false);
  const [csv, setCsv] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function actualizarDesdeMivau() {
    setActualizando(true);
    try {
      const r = await actualizar();
      if (r.ok) {
        toast.success(`MIVAU ${r.periodo}: ${r.filas} municipios actualizados`);
        resumen.refetch();
      } else toast.error(r.error);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo actualizar");
    } finally {
      setActualizando(false);
    }
  }

  async function enviarRegistradores() {
    setEnviando(true);
    try {
      const r = await importar({ data: { csv } });
      if (r.ok) {
        toast.success(
          `${r.importadas} filas importadas${r.errores.length ? `, ${r.errores.length} con errores` : ""}`,
        );
        setCsv("");
        resumen.refetch();
      } else toast.error(r.error);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo importar");
    } finally {
      setEnviando(false);
    }
  }

  const r = resumen.data;
  const estado = (x?: { periodo: string | null; zonas: number }) =>
    x?.periodo ? `${x.zonas} zonas · ${x.periodo}` : "sin datos";

  return (
    <Card>
      <CardContent className="grid gap-6 p-6">
        <div>
          <h2 className="text-base font-semibold">
            Referencias de cierre por municipio y provincia
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Se usan para corregir el precio de cierre de las ventas cuando no hay dato del Notariado
            para el código postal: primero el valor tasado del municipio (MIVAU), después el precio
            registrado en la provincia (Registradores).
          </p>
        </div>

        <div className="grid gap-2">
          <h3 className="text-sm font-semibold">MIVAU · valor tasado por municipio</h3>
          <p className="text-xs text-muted-foreground">
            Municipios de más de 25.000 habitantes, trimestral. Se descarga directamente del
            Ministerio. Estado:{" "}
            {resumen.isLoading ? "cargando…" : r && r.ok ? estado(r.mivau) : "—"}
          </p>
          <div>
            <Button onClick={actualizarDesdeMivau} disabled={actualizando} variant="outline">
              {actualizando ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Actualizar desde el MIVAU
            </Button>
          </div>
        </div>

        <div className="grid gap-2">
          <h3 className="text-sm font-semibold">Registradores · precio registrado por provincia</h3>
          <p className="text-xs text-muted-foreground">
            Tabla de provincias de la Estadística Registral Inmobiliaria (sólo se publica en PDF).
            Formato <code>provincia;periodo;eur_m2</code>, por ejemplo{" "}
            <code>Valencia/València;2025-T2;1607</code>. Estado:{" "}
            {resumen.isLoading ? "cargando…" : r && r.ok ? estado(r.registradores) : "—"}
          </p>
          <Textarea
            rows={4}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={"provincia;periodo;eur_m2\nValencia/València;2025-T2;1607"}
            className="font-mono text-xs"
          />
          <div>
            <Button onClick={enviarRegistradores} disabled={!csv.trim() || enviando}>
              {enviando ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Importar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
